import { Box, type SxProps, type Theme, useMediaQuery, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import { motion } from "framer-motion";
import type { ReactElement } from "react";
import { ACCENT_STRIP_HEIGHT, ALPHA, BOTTOM_NAV, MOTION, SHADOW, SURFACE, TOUCH_TARGET_MIN } from "@/theme/tokens";
import { SystemTabBadgeIndicator } from "@/ui/Badges/SystemTabBadgeIndicator";
import type { SystemTabBadge } from "@/ui/Clickable/TabsAndChips/SystemTabs/utils";

/** One destination in the bottom bar. Labels arrive already-translated. */
export type BottomNavItem<T> = {
	/** Stable value used for selection (and as the per-item test-id suffix). */
	value: T;
	/** Already-translated caption under the glyph — also the button's accessible name. */
	label: string;
	/** Glyph — sized by the bar, so pass an unstyled icon element. */
	icon: ReactElement;
	disabled?: boolean;
	/** Optional count-pill / status-dot, drawn over the glyph's corner (same shape as `SystemTabs`). */
	badge?: SystemTabBadge;
	/** Per-item test id (falls back to `{dataTestId}-item-{value}` when omitted). */
	dataTestId?: string;
};

type Props<T> = {
	items: BottomNavItem<T>[];
	/** The selected item, or `undefined` on a route the bar does not own — then no slot is marked current. */
	activeValue: T | undefined;
	onChange: (next: T) => void;
	/** Accessible label for the `<nav>` landmark. */
	ariaLabel?: string;
	/**
	 * Glyphs only, each caption kept for screen readers — for when the captions cannot fit their slots (Android's
	 * larger font sizes), where an ellipsis would leave every caption a fragment. Absent, captions show.
	 */
	hideLabels?: boolean;
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

const INDICATOR_LAYOUT_ID = "system-bottom-nav-active";

/**
 * How far the active pill sits inside its slot (px). Top clears the accent notch on the bar's edge;
 * the sides leave a visible gutter between neighbouring slots so the pill reads as one item, not a
 * column of a table.
 */
const INDICATOR_INSET = { TOP: 6, SIDE: 8, BOTTOM: 4 } as const;

/**
 * `SystemBottomNav` — the generic phone bottom navigation bar: a row of equal-width icon + label
 * destinations with an animated active indicator. The horizontal twin of `SystemSideRail` (same item
 * shape, same `SystemTabBadge`, same shared-`layoutId` indicator), for the viewport where a rail does
 * not fit and a thumb is the pointer.
 *
 * Generic over the selection value `T` and controlled (`activeValue` + `onChange`). Semantic `<nav>` +
 * `<button aria-current="page">`, every slot at least `TOUCH_TARGET_MIN` tall, and the iOS
 * home-indicator inset paid by the bar itself — callers reserve `BOTTOM_NAV.TOTAL_HEIGHT_CSS`, which
 * already includes it.
 *
 * **Layout-neutral, like the rail: the caller positions it** (this is what lets the same bar be fixed
 * over a scroller on one screen and sit in a flex column on another) — and, when it is pinned over a
 * modal surface, the caller also owns its stacking. It draws itself as a bar: opaque, hairline top
 * border and the one upward-casting shadow (`SHADOW.BAR_TOP`) so content scrolls *under* it.
 *
 * The active slot takes a brand-tinted pill plus a short accent bar on the bar's top edge; the pill
 * SLIDES between slots via a shared Framer `layoutId`, and reduced motion drops the slide (the tint
 * stays). Presses scale 0.97 — the house press feedback, and the only affordance a touch-only device
 * gets, since the hover wash is gated to fine pointers.
 */
export const SystemBottomNav = <T,>({ items, activeValue, onChange, ariaLabel, hideLabels, sx, dataTestId }: Props<T>): ReactElement => {
	const theme = useTheme();
	const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
	const accent = theme.palette.primary.main;

	return (
		<Box
			component="nav"
			aria-label={ariaLabel}
			data-testid={dataTestId}
			sx={[
				{
					width: "100%",
					flexShrink: 0,
					display: "flex",
					flexDirection: "column",
					// Border-box on an explicit TOTAL height, so what the bar occupies is exactly what the
					// screen above it reserves — a 1px top border outside that box is 1px of the last row
					// permanently under the bar.
					boxSizing: "border-box",
					height: BOTTOM_NAV.TOTAL_HEIGHT_CSS,
					bgcolor: SURFACE,
					borderTop: "1px solid",
					borderColor: "divider",
					boxShadow: SHADOW.BAR_TOP,
					// The bar pays the home-indicator inset so its slots stay tappable on a notched phone.
					pb: "env(safe-area-inset-bottom, 0px)",
				},
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", alignItems: "stretch", flex: 1, minHeight: 0 }}>
				{items.map((item) => {
					const selected = item.value === activeValue;
					const itemTestId = item.dataTestId ?? (dataTestId ? `${dataTestId}-item-${String(item.value)}` : undefined);
					return (
						<Box component="li" key={String(item.value)} sx={{ position: "relative", flex: 1, minWidth: 0 }}>
							{/* Sliding active highlight — one shared layoutId, so it travels from the previously
							    active slot to this one. Sits behind the button and is never a hit target. */}
							{selected && (
								<Box
									component={motion.div}
									layoutId={reduceMotion ? undefined : INDICATOR_LAYOUT_ID}
									transition={{ type: "spring", stiffness: 520, damping: 40 }}
									sx={{
										position: "absolute",
										inset: 0,
										// A rounded, inset pill rather than a full-bleed tint: the slot's edges are the
										// bar's own grid, and filling them corner to corner reads as a grey box instead
										// of a selected item.
										"&::before": {
											content: '""',
											position: "absolute",
											inset: `${INDICATOR_INSET.TOP}px ${INDICATOR_INSET.SIDE}px ${INDICATOR_INSET.BOTTOM}px`,
											borderRadius: 2,
											backgroundColor: alpha(accent, ALPHA.BRAND_TINT),
										},
										// Accent notch hanging off the bar's top edge — the "you are here" mark that
										// survives at a glance, where a tint alone reads as noise on a white bar.
										"&::after": {
											content: '""',
											position: "absolute",
											top: 0,
											left: "50%",
											transform: "translateX(-50%)",
											width: "40%",
											height: ACCENT_STRIP_HEIGHT,
											borderRadius: `0 0 ${ACCENT_STRIP_HEIGHT}px ${ACCENT_STRIP_HEIGHT}px`,
											backgroundColor: accent,
										},
									}}
								/>
							)}
							<Box
								component="button"
								type="button"
								disabled={item.disabled}
								aria-current={selected ? "page" : undefined}
								data-testid={itemTestId}
								onClick={() => !item.disabled && onChange(item.value)}
								sx={{
									position: "relative",
									width: "100%",
									height: "100%",
									minHeight: TOUCH_TARGET_MIN,
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									gap: 0.25,
									px: 0.5,
									border: "none",
									bgcolor: "transparent",
									color: selected ? accent : "text.secondary",
									font: "inherit",
									cursor: item.disabled ? "not-allowed" : "pointer",
									opacity: item.disabled ? 0.45 : 1,
									transition: `color ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}, transform ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
									"@media (hover: hover) and (pointer: fine)": {
										"&:hover": { color: item.disabled ? undefined : accent },
									},
									"&:active": { transform: item.disabled ? "none" : "scale(0.97)" },
									"&:focus-visible": { outline: `2px solid ${alpha(accent, ALPHA.FOCUS_RING)}`, outlineOffset: -2 },
									"& svg": { fontSize: BOTTOM_NAV.ICON_SIZE, display: "block" },
								}}
							>
								<Box component="span" sx={{ position: "relative", display: "inline-flex" }}>
									{item.icon}
									{item.badge && (
										// Over the glyph's corner rather than after the label: a slot is ~a quarter of a
										// phone, and an inline badge would push the caption into an ellipsis.
										<Box component="span" sx={{ position: "absolute", top: 0, right: 0, transform: "translate(50%, -45%)" }}>
											<SystemTabBadgeIndicator badge={item.badge} />
										</Box>
									)}
								</Box>
								<Box
									component="span"
									sx={
										hideLabels
											? visuallyHidden
											: {
													maxWidth: "100%",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
													fontSize: BOTTOM_NAV.LABEL_FONT_SIZE,
													fontWeight: selected ? 700 : 500,
													lineHeight: 1.2,
												}
									}
								>
									{item.label}
								</Box>
							</Box>
						</Box>
					);
				})}
			</Box>
		</Box>
	);
};
